import { useQuery } from '@apollo/client'
import { Box, Chip, Stack } from '@mui/material'
import { type FC } from 'react'
import {
    OutreachStatusFilter,
    RecruitingStatusFilter,
    SystemStatusFilter,
} from '__generated__/graphql'
import {
    GetPreferenceCompanyOptions,
    GetPreferenceIndustryOptions,
    GetPreferenceLocationOptions,
    GetPreferenceSectorOptions,
} from 'recruiter/graphql/companies.gql'
import { type CandidateFilterState } from 'shared/hooks/candidateSearchUrlState'
import { GetStartDateOptions } from 'shared/graphql/startDates.gql'
import {
    PreferenceFilter,
    TwoTierPreferenceFilter,
} from './CandidateMinimalFilters'
import { FilterWrapper } from './FilterPrimitives'
import { toggleChipSx } from './minimalFilterStyles'

const RECRUITING_STATUS_OPTIONS: {
    value: RecruitingStatusFilter
    label: string
}[] = [
    {
        value: RecruitingStatusFilter.ActivelyRecruiting,
        label: 'Actively Recruiting',
    },
    { value: RecruitingStatusFilter.FinalStages, label: 'Final Stages' },
    { value: RecruitingStatusFilter.Opportunistic, label: 'Opportunistic' },
    { value: RecruitingStatusFilter.HoldingOut, label: 'Holding Out' },
    { value: RecruitingStatusFilter.NotRecruiting, label: 'Not Recruiting' },
    { value: RecruitingStatusFilter.DoNotCall, label: 'Do Not Call' },
    { value: RecruitingStatusFilter.NoStatus, label: 'No Status' },
]

const OUTREACH_STATUS_OPTIONS: {
    value: OutreachStatusFilter
    label: string
}[] = [
    { value: OutreachStatusFilter.TreadCarefully, label: 'Tread Carefully' },
    { value: OutreachStatusFilter.NonPoach, label: 'Non-Poach' },
    { value: OutreachStatusFilter.CpiPlaced, label: 'CPI Placed' },
    { value: OutreachStatusFilter.NoStatus, label: 'No Status' },
]

const SYSTEM_STATUS_OPTIONS: {
    value: SystemStatusFilter
    label: string
}[] = [
    {
        value: SystemStatusFilter.EmailedInvitation,
        label: 'Emailed Invitation',
    },
    { value: SystemStatusFilter.Unregistered, label: 'Unregistered' },
    { value: SystemStatusFilter.OnHold, label: 'On Hold' },
    { value: SystemStatusFilter.InProcess, label: 'In Process' },
    { value: SystemStatusFilter.SentScheduler, label: 'Sent Scheduler' },
    { value: SystemStatusFilter.Met, label: 'Met' },
    { value: SystemStatusFilter.NoStatus, label: 'No Status' },
]

const RecruitingStatusChips: FC<{
    value: RecruitingStatusFilter[]
    onChange: (value: RecruitingStatusFilter[]) => void
}> = ({ value, onChange }) => {
    const toggle = (status: RecruitingStatusFilter) => {
        if (value.includes(status)) {
            onChange(value.filter((s) => s !== status))
        } else {
            onChange([...value, status])
        }
    }

    return (
        <FilterWrapper label="Recruiting Status">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {RECRUITING_STATUS_OPTIONS.map((opt) => (
                    <Chip
                        key={opt.value}
                        label={opt.label}
                        size="small"
                        onClick={() => toggle(opt.value)}
                        sx={toggleChipSx(value.includes(opt.value))}
                    />
                ))}
            </Box>
        </FilterWrapper>
    )
}

const OutreachStatusChips: FC<{
    value: OutreachStatusFilter[]
    onChange: (value: OutreachStatusFilter[]) => void
}> = ({ value, onChange }) => {
    const toggle = (status: OutreachStatusFilter) => {
        if (value.includes(status)) {
            onChange(value.filter((s) => s !== status))
        } else {
            onChange([...value, status])
        }
    }

    return (
        <FilterWrapper label="Off Limits Status">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {OUTREACH_STATUS_OPTIONS.map((opt) => (
                    <Chip
                        key={opt.value}
                        label={opt.label}
                        size="small"
                        onClick={() => toggle(opt.value)}
                        sx={toggleChipSx(value.includes(opt.value))}
                    />
                ))}
            </Box>
        </FilterWrapper>
    )
}

const SystemStatusChips: FC<{
    value: SystemStatusFilter[]
    onChange: (value: SystemStatusFilter[]) => void
}> = ({ value, onChange }) => {
    const toggle = (status: SystemStatusFilter) => {
        if (value.includes(status)) {
            onChange(value.filter((s) => s !== status))
        } else {
            onChange([...value, status])
        }
    }

    return (
        <FilterWrapper label="System Status">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {SYSTEM_STATUS_OPTIONS.map((opt) => (
                    <Chip
                        key={opt.value}
                        label={opt.label}
                        size="small"
                        onClick={() => toggle(opt.value)}
                        sx={toggleChipSx(value.includes(opt.value))}
                    />
                ))}
            </Box>
        </FilterWrapper>
    )
}

const StartDateChips: FC<{
    value: string[]
    onChange: (value: string[]) => void
    options: string[]
    loading: boolean
}> = ({ value, onChange, options, loading }) => {
    const toggle = (startDate: string) => {
        if (value.includes(startDate)) {
            onChange(value.filter((s) => s !== startDate))
        } else {
            onChange([...value, startDate])
        }
    }

    if (loading) return null

    return (
        <FilterWrapper label="Preferred Start Date">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {options.map((opt) => (
                    <Chip
                        key={opt}
                        label={opt}
                        size="small"
                        onClick={() => toggle(opt)}
                        sx={toggleChipSx(value.includes(opt))}
                    />
                ))}
            </Box>
        </FilterWrapper>
    )
}

interface RecruitingStatusFilterSectionProps {
    filters: CandidateFilterState
    updateFilters: (patch: Partial<CandidateFilterState>) => void
}

export const RecruitingStatusFilterSection: FC<
    RecruitingStatusFilterSectionProps
> = ({ filters, updateFilters }) => {
    const industryQuery = useQuery(GetPreferenceIndustryOptions)
    const sectorQuery = useQuery(GetPreferenceSectorOptions)
    const companyQuery = useQuery(GetPreferenceCompanyOptions)
    const locationQuery = useQuery(GetPreferenceLocationOptions)
    const startDateQuery = useQuery(GetStartDateOptions)

    return (
        <Stack gap={2.5} sx={{ p: 2 }}>
            <RecruitingStatusChips
                value={filters.recruitingStatus}
                onChange={(recruitingStatus) =>
                    updateFilters({ recruitingStatus })
                }
            />
            <OutreachStatusChips
                value={filters.outreachStatus}
                onChange={(outreachStatus) => updateFilters({ outreachStatus })}
            />
            <SystemStatusChips
                value={filters.systemStatus}
                onChange={(systemStatus) => updateFilters({ systemStatus })}
            />
            <PreferenceFilter
                label="Industry Preference"
                first={filters.industryPrefFirst}
                second={filters.industryPrefSecond}
                third={filters.industryPrefThird}
                onChange={(first, second, third) =>
                    updateFilters({
                        industryPrefFirst: first,
                        industryPrefSecond: second,
                        industryPrefThird: third,
                    })
                }
                options={industryQuery.data?.preferenceIndustries ?? []}
                loading={industryQuery.loading}
            />
            <PreferenceFilter
                label="Sector Preference"
                first={filters.sectorPrefFirst}
                second={filters.sectorPrefSecond}
                third={filters.sectorPrefThird}
                onChange={(first, second, third) =>
                    updateFilters({
                        sectorPrefFirst: first,
                        sectorPrefSecond: second,
                        sectorPrefThird: third,
                    })
                }
                options={sectorQuery.data?.preferenceSectors ?? []}
                loading={sectorQuery.loading}
            />
            <TwoTierPreferenceFilter
                label="Target Firm Preference"
                first={filters.companyPrefFirst}
                second={filters.companyPrefSecond}
                onChange={(first, second) =>
                    updateFilters({
                        companyPrefFirst: first,
                        companyPrefSecond: second,
                    })
                }
                options={companyQuery.data?.preferenceCompanies ?? []}
                loading={companyQuery.loading}
            />
            <PreferenceFilter
                label="Location Preference"
                first={filters.locationPrefFirst}
                second={filters.locationPrefSecond}
                third={filters.locationPrefThird}
                onChange={(first, second, third) =>
                    updateFilters({
                        locationPrefFirst: first,
                        locationPrefSecond: second,
                        locationPrefThird: third,
                    })
                }
                options={locationQuery.data?.preferenceLocations ?? []}
                loading={locationQuery.loading}
            />
            <StartDateChips
                value={filters.prefStartDates}
                onChange={(prefStartDates) => updateFilters({ prefStartDates })}
                options={startDateQuery.data?.candidateStartDates ?? []}
                loading={startDateQuery.loading}
            />
        </Stack>
    )
}
